import { useEffect, useState } from 'react'
import { useRecordContext } from 'react-admin'
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Typography,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import './VehicleAdjustments.scss'
import DamageSelector from '../QuoteRequest/DamageSelector'
import PropTypes from 'prop-types'
import Adjustment from './Adjustment'
import RemainingMOT from './remainingMOT'
import { useAdjustmentStatusContext } from '../../Utils/providers/AdjustmentStatusProvider'

const checkVehicleHasDamage = async (leadID) => {
  const entrypoint = process.env.REACT_APP_API_PLATFORM_ENDPOINT

  const response = await fetch(
    `${entrypoint}/services/quote/check-vehicle-has-damage?leadID=${leadID}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    }
  )

  const json = await response.json()

  if (json.status === 'error') {
    throw new Error(json.error_msg || 'error message')
  }

  return json
}

const DamageModal = ({
  sortedAdjustments,
  damageModalOpen,
  setDamageModalOpen,
}) => {
  return (
    <Dialog
      open={damageModalOpen}
      fullWidth
      maxWidth={'xl'}
      onClose={() => setDamageModalOpen(false)}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography fontSize={18} textTransform={'uppercase'}>
          Damage Report
        </Typography>
        <IconButton
          sx={{ cursor: 'pointer' }}
          onClick={() => setDamageModalOpen(false)}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <Typography variant="subtitle1">
            Please click on the area of the car and then select the &quot;damage
            type&quot; from the pop up list. You can repeat this process for as
            many defects as necessary.
          </Typography>
        </DialogContentText>
        <Box
          className="vehicle-adjustments vehicle-adjustments__general"
          sx={{ ml: 'calc(50% + .5rem)' }}
        >
          <Box>
            <Adjustment
              adjustment={
                sortedAdjustments[0].adjustments['Showroom Condition']
                  .adjustment
              }
              adjustmentSettings={
                sortedAdjustments[0].adjustments['Showroom Condition']
                  .adjustmentSettings
              }
              key={18}
            />
          </Box>
        </Box>
        <DamageSelector />
      </DialogContent>
    </Dialog>
  )
}

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <Grid hidden={value !== index} {...other}>
      {value === index && children}
    </Grid>
  )
}

const VehicleAdjustments = ({ sortedAdjustments }) => {
  const record = useRecordContext()
  const { refresh: refreshAdjustmentStatus } = useAdjustmentStatusContext()
  const [isButtonActive, setIsButtonActive] = useState(false)
  const [damageModalOpen, setDamageModalOpen] = useState(false)
  const vehicleState = {
    engineDoesntRun:
      sortedAdjustments[0].adjustments['Engine Condition'].adjustment.value ===
      3,
    insuranceWriteOff: [2, 3, 4, 5].includes(
      sortedAdjustments[0].adjustments['Insurance'].adjustment.value
    ),
  }

  if (!record.leadContact.vehicle.vehicleSalvage) return

  useEffect(() => {
    if (!record) return

    const handleCheckVehicleDamage = async () => {
      try {
        const hasDamage = await checkVehicleHasDamage(record.originId)
        // After checking damage, refresh the adjustment status to allow the button to be pressed
        await refreshAdjustmentStatus('Damage Changed')

        if (hasDamage.result === 'false') {
          throw new Error('No damage list found')
        }

        // On success allow button press
        setIsButtonActive(true)
      } catch (error) {
        setIsButtonActive(false)
      }
    }

    // Run on load to activate button if possible
    handleCheckVehicleDamage()

    window.addEventListener('checkVehicleDamage', handleCheckVehicleDamage)

    return () =>
      window.removeEventListener('checkVehicleDamage', handleCheckVehicleDamage)
  }, [])

  return (
    <Grid item xs={12}>
      <Grid
        container
        className="vehicle-adjustments__general"
        rowSpacing={1}
        columnSpacing={2}
      >
        <Grid item>
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['Engine Condition'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Engine Condition']
                .adjustmentSettings
            }
            key={'vehicle-general' + 1}
          />
        </Grid>
        <Grid
          item
          sx={{
            opacity: vehicleState.engineDoesntRun ? 1 : 0.5,
            pointerEvents: vehicleState.engineDoesntRun ? 'auto' : 'none',
            transition: 'opacity 0.2s ease',
          }}
        >
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['Dismantled'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Dismantled'].adjustmentSettings
            }
            key={'vehicle-general' + 2}
          />
        </Grid>
        <Grid item>
          <Adjustment
            adjustment={sortedAdjustments[0].adjustments['Gear Box'].adjustment}
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Gear Box'].adjustmentSettings
            }
            key={'vehicle-general' + 3}
          />
        </Grid>
        <Grid item>
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['Insurance'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Insurance'].adjustmentSettings
            }
            key={'vehicle-general' + 4}
          />
        </Grid>
        <Grid
          item
          sx={{
            opacity: vehicleState.insuranceWriteOff ? 1 : 0.5,
            pointerEvents: vehicleState.insuranceWriteOff ? 'auto' : 'none',
            transition: 'opacity 0.2s ease',
          }}
        >
          <Adjustment
            adjustment={sortedAdjustments[0].adjustments['Repaired'].adjustment}
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Repaired'].adjustmentSettings
            }
            key={'vehicle-general' + 5}
          />
        </Grid>
        <Grid item>
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['Crash Damaged'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Crash Damaged']
                .adjustmentSettings
            }
            key={'vehicle-general' + 6}
          />
        </Grid>
        <Grid item width="100%">
          <Divider sx={{ my: 2 }} />
        </Grid>{' '}
        <Grid item>
          <Adjustment
            adjustment={sortedAdjustments[0].adjustments['Interior'].adjustment}
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Interior'].adjustmentSettings
            }
            key={1}
          />
        </Grid>
        <Grid
          item
          sx={{
            display: record.leadContact.vehicle.vehicleSalvage
              .offerJourneyFollowed
              ? 'block'
              : 'none',
          }}
        >
          <Adjustment
            adjustment={sortedAdjustments[0].adjustments['Leather'].adjustment}
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Leather'].adjustmentSettings
            }
            key={2}
          />
        </Grid>
        <Grid
          item
          sx={{
            display: record.leadContact.vehicle.vehicleSalvage
              .offerJourneyFollowed
              ? 'block'
              : 'none',
          }}
        >
          <Adjustment
            adjustment={sortedAdjustments[0].adjustments['SAT Nav'].adjustment}
            adjustmentSettings={
              sortedAdjustments[0].adjustments['SAT Nav'].adjustmentSettings
            }
            key={3}
          />
        </Grid>
        <Grid
          item
          sx={{
            display: record.leadContact.vehicle.vehicleSalvage
              .offerJourneyFollowed
              ? 'block'
              : 'none',
          }}
        >
          <Adjustment
            adjustment={sortedAdjustments[0].adjustments['Keys'].adjustment}
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Keys'].adjustmentSettings
            }
            key={4}
          />
        </Grid>
        <Grid
          item
          sx={{
            display: record.leadContact.vehicle.vehicleSalvage
              .offerJourneyFollowed
              ? 'block'
              : 'none',
          }}
        >
          <Adjustment
            adjustment={sortedAdjustments[0].adjustments['History'].adjustment}
            adjustmentSettings={
              sortedAdjustments[0].adjustments['History'].adjustmentSettings
            }
            key={5}
          />
        </Grid>
        <Grid item>
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['MOT Fault'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['MOT Fault'].adjustmentSettings
            }
            key={13}
          />
        </Grid>
        <Grid item>
          <RemainingMOT />
        </Grid>
        <Grid
          className="vehicle-adjustments__lights"
          item
          style={{
            display: 'flex',
            alignItems: 'end',
          }}
        >
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['EML Light'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['EML Light'].adjustmentSettings
            }
            key={'vehicle-general' + 7}
          />
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['Airbag Light'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Airbag Light']
                .adjustmentSettings
            }
            key={'vehicle-general' + 8}
          />
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['ABS Light'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['ABS Light'].adjustmentSettings
            }
            key={'vehicle-general' + 9}
          />
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['Other Light'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['Other Light'].adjustmentSettings
            }
            key={'vehicle-general' + 10}
          />
          <Adjustment
            adjustment={
              sortedAdjustments[0].adjustments['No Warning Lights'].adjustment
            }
            adjustmentSettings={
              sortedAdjustments[0].adjustments['No Warning Lights']
                .adjustmentSettings
            }
            key={'vehicle-general' + 1}
          />
        </Grid>
        <Grid item width="100%">
          <Divider sx={{ my: 2 }} />
        </Grid>
        <Grid item>
          <label>Scratches/ Dents</label>
          <br />
          <Button
            className="open-damage"
            onClick={() => setDamageModalOpen(!damageModalOpen)}
            data-confirmed={isButtonActive}
            data-selected={isButtonActive}
          >
            Open damage report
          </Button>
        </Grid>
        <Grid item width="100%">
          <Divider sx={{ my: 2 }} />
        </Grid>
        <Grid
          className="vehicle-adjustments__faults"
          item
          style={{ width: '100%' }}
        >
          <Grid
            container
            spacing={0}
            display="grid"
            style={{
              gridTemplateColumns: 'repeat(6, 1fr)',
            }}
          >
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Steering Fault'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Steering Fault']
                    .adjustmentSettings
                }
                key={6}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Wiper Fault'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Wiper Fault']
                    .adjustmentSettings
                }
                key={7}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Brake Fault'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Brake Fault']
                    .adjustmentSettings
                }
                key={8}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Door Fault'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Door Fault']
                    .adjustmentSettings
                }
                key={9}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Window Fault'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Window Fault']
                    .adjustmentSettings
                }
                key={10}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Exhaust Fault'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Exhaust Fault']
                    .adjustmentSettings
                }
                key={11}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Convertible Fault']
                    .adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Convertible Fault']
                    .adjustmentSettings
                }
                key={12}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Blower Fault'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Blower Fault']
                    .adjustmentSettings
                }
                key={14}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Water Leak'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Water Leak']
                    .adjustmentSettings
                }
                key={15}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Airbag Fault'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Airbag Fault']
                    .adjustmentSettings
                }
                key={16}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Major Interior fault']
                    .adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Major Interior fault']
                    .adjustmentSettings
                }
                key={17}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['Other fault'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['Other fault']
                    .adjustmentSettings
                }
                key={17}
              />
            </Grid>
            <Grid item>
              <Adjustment
                adjustment={
                  sortedAdjustments[0].adjustments['No other faults'].adjustment
                }
                adjustmentSettings={
                  sortedAdjustments[0].adjustments['No other faults']
                    .adjustmentSettings
                }
                key={17}
              />
            </Grid>
          </Grid>
        </Grid>
        <DamageModal
          sortedAdjustments={sortedAdjustments}
          damageModalOpen={damageModalOpen}
          setDamageModalOpen={setDamageModalOpen}
        />
      </Grid>
    </Grid>
  )
}

DamageModal.propTypes = {
  sortedAdjustments: PropTypes.array,
  damageModalOpen: PropTypes.bool,
  setDamageModalOpen: PropTypes.func,
}

TabPanel.propTypes = {
  children: PropTypes.any,
  value: PropTypes.any,
  index: PropTypes.any,
}
VehicleAdjustments.propTypes = {
  sortedAdjustments: PropTypes.array,
}

export default VehicleAdjustments
